/**
 * Gallery theme registry — one preset per design, transcribed from
 * design_handoff_client_galleries/README.md ("The 23 designs") and
 * each file's <helmet>. Fonts, ground/ink/accent, and the layout
 * family are FINAL INTENT from the handoff; copy is not.
 *
 * Mirrors swiftui/GalleryTheme.swift so web and iOS stay in lockstep.
 * Adding a design = one entry here + one slug in the migration's
 * CHECK constraint + a template component.
 */
import type { GalleryBackgroundId, GalleryTemplateId } from './types';

export type FontFallback = 'serif' | 'sans-serif' | 'monospace';

export interface GalleryFont {
  /** Google Fonts family name, e.g. "Instrument Serif". */
  family: string;
  weights?: number[];
  italic?: boolean;
  fallback: FontFallback;
  /** Not on Google Fonts (11 Swiss Grid is system Helvetica). */
  system?: boolean;
}

export type GalleryLayout =
  | 'basic'
  | 'editorial'
  | 'cinema'
  | 'masonry'
  | 'scroll'
  | 'filmstrip'
  | 'bento'
  | 'storybook'
  | 'lookbook'
  | 'exhibition'
  | 'tracklist'
  | 'swiss'
  | 'masthead'
  | 'quarterly'
  | 'zine'
  | 'numero'
  | 'gatefold'
  | 'contact-sheet'
  | 'contents'
  | 'newsstand'
  | 'review-room'
  | 'premiere'
  | 'dailies'
  | 'story-reel';

export interface GalleryTheme {
  id: GalleryTemplateId;
  /** "01" … "23" — the handoff numbering, shown in the picker. */
  number: string;
  name: string;
  kind: 'photo' | 'video';
  /** Handoff rounds: 1 photo · 2 micro-interactions · 3 magazine · 4 magazine+browsing · 5 video. */
  round: 0 | 1 | 2 | 3 | 4 | 5;
  /** One-line signature for the picker card. */
  signature: string;
  layout: GalleryLayout;
  display: GalleryFont;
  body: GalleryFont;
  mono?: GalleryFont;
  /** Page ground. */
  bg: string;
  /** Primary text. */
  ink: string;
  /** Signature accent. */
  accent: string;
  /** Secondary text (captions, meta). */
  muted: string;
  /** Optional second surface (cards, floors, sidebars). */
  surface?: string;
  isDark: boolean;
  /** Corner radius family used on tiles/buttons. */
  radius: number;
  /** Buttons are pills (999 radius). */
  pill: boolean;
}

const f = (
  family: string,
  fallback: FontFallback,
  weights?: number[],
  italic?: boolean,
): GalleryFont => ({ family, fallback, weights, italic });

export const GALLERY_THEMES: Record<GalleryTemplateId, GalleryTheme> = {

  // ── Round 0 — the default (GALLERY-2) ──────────────────────────
  // `surface` is Basic's hairline colour (the handoff's `t.line`).
  basic: {
    id: 'basic', number: '00', name: 'Basic', kind: 'photo', round: 0,
    signature: 'A cover photo, a clean square grid, nothing in the way.',
    layout: 'basic',
    display: f('Figtree', 'sans-serif', [400, 500, 600]), body: f('Figtree', 'sans-serif', [400, 500, 600]),
    bg: '#ffffff', ink: '#1a1a1a', accent: '#1a1a1a', muted: '#777777', surface: '#e8e8e8', isDark: false, radius: 4, pill: false,
  },


};

/** GALLERY-2 — Basic (00) is the default: a missing or unknown id renders it. */
export const DEFAULT_TEMPLATE_ID: GalleryTemplateId = 'basic';

export const getGalleryTheme = (id: GalleryTemplateId | string | null | undefined): GalleryTheme =>
  GALLERY_THEMES[(id as GalleryTemplateId) in GALLERY_THEMES ? (id as GalleryTemplateId) : DEFAULT_TEMPLATE_ID];

// ── GALLERY-2 — backgrounds, font pairings, covers ──────────────────

/** One ground a design can sit on: its colour and the ink that reads on it. */
export interface GalleryBackground {
  id: GalleryBackgroundId;
  label: string;
  bg: string;
  ink: string;
  /** Basic's own secondary text and hairline for this ground. */
  muted?: string;
  line?: string;
  isDark: boolean;
}

/**
 * The grounds a gallery can sit on.
 *
 * `ink` is the type colour, `muted` the secondary text, `line` the
 * hairline. They are picked per ground rather than derived, because a
 * single formula gives you grey type on a warm paper and it looks like
 * a bug.
 */
const GROUNDS: Record<GalleryBackgroundId, GalleryBackground> = {
  // ── paper ──────────────────────────────────────────────────────
  light:  { id: 'light',  label: 'White',  bg: '#ffffff', ink: '#1a1a1a', muted: '#777777', line: '#e8e8e8', isDark: false },
  ivory:  { id: 'ivory',  label: 'Ivory',  bg: '#faf7f2', ink: '#1f1c17', muted: '#847d72', line: '#ebe5db', isDark: false },
  cream:  { id: 'cream',  label: 'Cream',  bg: '#f6f2ea', ink: '#221f1a', muted: '#8a8378', line: '#e6e0d4', isDark: false },
  sand:   { id: 'sand',   label: 'Sand',   bg: '#f0e9df', ink: '#262019', muted: '#8b8073', line: '#ded4c6', isDark: false },
  blush:  { id: 'blush',  label: 'Blush',  bg: '#f7eeea', ink: '#241b18', muted: '#8d7d77', line: '#e9dad3', isDark: false },
  sage:   { id: 'sage',   label: 'Sage',   bg: '#eceee7', ink: '#1b1f1a', muted: '#7b8178', line: '#dbded3', isDark: false },
  mist:   { id: 'mist',   label: 'Mist',   bg: '#eef0f2', ink: '#171a1d', muted: '#767d84', line: '#dde1e5', isDark: false },
  grey:   { id: 'grey',   label: 'Grey',   bg: '#ededeb', ink: '#161616', muted: '#6f6f6b', line: '#d9d9d6', isDark: false },
  stone:  { id: 'stone',  label: 'Stone',  bg: '#d9d7d2', ink: '#1b1a18', muted: '#6c6a65', line: '#c4c1bb', isDark: false },

  // ── dark ───────────────────────────────────────────────────────
  slate:  { id: 'slate',  label: 'Slate',  bg: '#3a3f45', ink: '#f1f3f5', muted: '#a7afb7', line: '#4e545b', isDark: true },
  espresso: { id: 'espresso', label: 'Espresso', bg: '#1a1512', ink: '#f4efe8', muted: '#9b9087', line: '#2e2721', isDark: true },
  forest: { id: 'forest', label: 'Forest', bg: '#16211c', ink: '#eef3ef', muted: '#8ea097', line: '#28362f', isDark: true },
  plum:   { id: 'plum',   label: 'Plum',   bg: '#1d151f', ink: '#f2ecf3', muted: '#9c8ea0', line: '#2f2432', isDark: true },
  ink:    { id: 'ink',    label: 'Ink',    bg: '#14171c', ink: '#eef1f5', muted: '#8d95a0', line: '#242a31', isDark: true },
  charcoal: { id: 'charcoal', label: 'Charcoal', bg: '#1c1c1e', ink: '#f2f2f0', muted: '#9a9a96', line: '#333336', isDark: true },
  black:  { id: 'black',  label: 'Black',  bg: '#0a0a0a', ink: '#f4f4f2', muted: '#8c8c88', line: '#242424', isDark: true },
};

/** Every design's `background` tweak. */
export const GALLERY_BACKGROUNDS: Record<GalleryBackgroundId, GalleryBackground> = GROUNDS;

/** Basic's own grounds — white is its default. Same set. */
export const BASIC_BACKGROUNDS: Record<GalleryBackgroundId, GalleryBackground> = GROUNDS;

/** Relative luminance, 0–1. Rec. 709 coefficients. */
export function hexLuminance(hex: string): number {
  const h = hex.replace('#', '');
  if (h.length !== 6) return 1;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * A ground from any colour the studio picks.
 *
 * The sixteen presets have hand-picked ink, muted and line because one
 * formula gives grey type on warm paper. For an arbitrary colour there
 * is no hand to pick them, so they are derived — and deliberately as
 * translucent black or white rather than a hue, which reads correctly
 * on every background instead of only on neutrals.
 */
export function groundFromHex(hex: string): GalleryBackground {
  const dark = hexLuminance(hex) <= 0.55;
  return {
    id: 'light',          // never read; the hex is what renders
    label: 'Custom',
    bg: hex,
    ink: dark ? '#f5f5f3' : '#191919',
    muted: dark ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.54)',
    line: dark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.13)',
    isDark: dark,
  };
}

export const backgroundsFor = (id: GalleryTemplateId): Record<GalleryBackgroundId, GalleryBackground> =>
  id === 'basic' ? BASIC_BACKGROUNDS : GALLERY_BACKGROUNDS;

/**
 * A font pairing: a preset for the two faces. Stored as the same
 * `customization.fonts` the per-face pickers write (both faces are on the
 * whitelist), so nothing new rides the payload; `null`s are the design's
 * own faces ("Default").
 */
export interface GalleryFontPairing {
  id: string;
  label: string;
  display: string | null;
  body: string | null;
}

/** Every design's `fontPairing` tweak. */
/**
 * Type pairings. Every face is on the DISPLAY_FONTS / BODY_FONTS
 * whitelist in types.ts and has an entry in FONT_STYLES — a pairing
 * that asks Google Fonts for a weight a family doesn't have loses the
 * WHOLE stylesheet, the design's own faces included, so these are not
 * free to invent.
 *
 * Ordered by voice: sans, then the serifs, then the characterful ones.
 */
export const FONT_PAIRINGS: GalleryFontPairing[] = [
  { id: 'default',    label: 'Sans',       display: null,                 body: null },
  { id: 'grotesk',    label: 'Grotesk',    display: 'Archivo',            body: 'Familjen Grotesk' },
  { id: 'modern',     label: 'Modern',     display: 'Manrope',            body: 'Manrope' },
  { id: 'neutral',    label: 'Neutral',    display: 'Figtree',            body: 'Inter' },
  { id: 'serif',      label: 'Serif',      display: 'Cormorant Garamond', body: 'Figtree' },
  { id: 'garamond',   label: 'Garamond',   display: 'EB Garamond',        body: 'Karla' },
  { id: 'editorial',  label: 'Editorial',  display: 'DM Serif Display',   body: 'Space Grotesk' },
  { id: 'literary',   label: 'Literary',   display: 'Newsreader',         body: 'Public Sans' },
  { id: 'fashion',    label: 'Fashion',    display: 'Bodoni Moda',        body: 'Work Sans' },
  { id: 'warm',       label: 'Warm',       display: 'Fraunces',           body: 'Nunito Sans' },
  { id: 'statement',  label: 'Statement',  display: 'Instrument Serif',   body: 'Plus Jakarta Sans' },
  { id: 'condensed',  label: 'Condensed',  display: 'Bebas Neue',         body: 'Space Grotesk' },
  { id: 'geometric',  label: 'Geometric',  display: 'Syne',               body: 'Outfit' },
  { id: 'mono',       label: 'Mono',       display: 'JetBrains Mono',     body: 'JetBrains Mono' },
];

/** Basic uses the same set; its own Figtree is "Sans". */
export const BASIC_FONT_PAIRINGS: GalleryFontPairing[] = FONT_PAIRINGS;

export const pairingsFor = (id: GalleryTemplateId): GalleryFontPairing[] =>
  id === 'basic' ? BASIC_FONT_PAIRINGS : FONT_PAIRINGS;

/** The pairing these faces are, or null when they're a hand-picked mix. */
export const pairingFor = (
  id: GalleryTemplateId,
  fonts: { display?: string | null; body?: string | null } | undefined,
): GalleryFontPairing | null => {
  const display = fonts?.display ?? null;
  const body = fonts?.body ?? null;
  return pairingsFor(id).find((p) => p.display === display && p.body === body) ?? null;
};

/**
 * GALLERY-2 — each design's cover: a 3:4 composition of its signature
 * layout (the handoff's covers/, no text), shown in place of a live
 * render wherever designs are picked or shown off.
 */
export const designCoverSrc = (id: GalleryTemplateId | string): string =>
  `/gallery-designs/covers/${(id as GalleryTemplateId) in GALLERY_THEMES ? id : DEFAULT_TEMPLATE_ID}.jpg`;

/** Ordered list for the picker — handoff order. */
export const PICKER_THEMES: GalleryTheme[] = Object.values(GALLERY_THEMES)
  .sort((a, b) => a.number.localeCompare(b.number));

/** `font-family` stack for a theme font. */
export const fontStack = (font: GalleryFont): string =>
  `'${font.family}', ${font.fallback}`;

/**
 * One Google Fonts stylesheet URL for a set of fonts (deduped by
 * family). Idempotent to inject: callers key the <link> on the href.
 */
export const fontsHref = (input: Array<GalleryFont | undefined>): string | null => {
  const fonts = input.filter((x): x is GalleryFont => !!x && !x.system);
  if (fonts.length === 0) return null;
  // One entry per family with the UNION of the weights asked for. Designs
  // that set display and body in the same family (Masonry's Manrope, Neon
  // Grid's Archivo, Review Room's Sora, Dailies' Chivo) used to load only
  // the display weights, so body text drew in the nearest heavy cut.
  const merged = new Map<string, { weights: Set<number>; italic: boolean }>();
  for (const font of fonts) {
    const entry = merged.get(font.family) ?? { weights: new Set<number>(), italic: false };
    for (const w of font.weights && font.weights.length > 0 ? font.weights : [400]) entry.weights.add(w);
    entry.italic = entry.italic || !!font.italic;
    merged.set(font.family, entry);
  }
  const families: string[] = [];
  for (const [family, entry] of merged) {
    const fam = family.replace(/ /g, '+');
    const weights = [...entry.weights].sort((a, b) => a - b);
    if (entry.italic) {
      const axis = weights.flatMap((w) => [`0,${w}`, `1,${w}`]).join(';');
      families.push(`family=${fam}:ital,wght@${axis}`);
    } else {
      families.push(`family=${fam}:wght@${weights.join(';')}`);
    }
  }
  return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
};

/** Everything one theme needs (display + body + mono). */
export const googleFontsHref = (theme: GalleryTheme): string | null =>
  fontsHref([theme.display, theme.body, theme.mono]);

/** The display face of every pickable design, for the editor's picker. */
export const pickerFontsHref = (): string | null =>
  fontsHref([
    ...PICKER_THEMES.map((t) => ({ ...t.display, weights: [400, 700], italic: false })),
    ...PICKER_THEMES.map((t) => ({ ...t.body, weights: [400], italic: false })),
  ]);
